/* eslint-disable @typescript-eslint/naming-convention */
import React, { useState, useEffect, FC } from 'react';
import { message, Spin } from 'antd';
import { Button, DetailsContainer, Dropdown } from '@ui-modules';
import { sortBy, values } from 'lodash';
import SubmissionDeadline from '@components/SubmissionDeadline';
import OverviewList from '@pages/RequestsDetailsPage/components/QuotationManagement/components/OverviewList';
import LineItemAssignment from '@pages/RequestsDetailsPage/components/QuotationManagement/components/LineItemAssignment';
import { useRepository } from '@context/repository.context';
import { useMutation, useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { IDetailsRequest, IProvidersQuotationResponse } from '@common/interfaces';
import { IDetailsUpdate, ISelectedItems } from '@common/interfaces/Common';
import ConfirmationModal from '@pages/RequestsDetailsPage/components/QuotationManagement/components/ConfirmationModal';
import {
  getCurrencies,
  getDestinations,
} from '@pages/RequestsDetailsPage/components/QuotationManagement/utils';
import AssignmentDetails from '@pages/RequestsDetailsPage/components/QuotationManagement/components/AssignmentDetails';
import {
  getDropdownStatusComponentProps,
  onErrorHandler,
  statusDropdownCustomStyles,
} from '@pages/RequestsDetailsPage/components/RequestsDetails/utils';

interface IQuotationManagement {
  details: IDetailsRequest;
}

const QuotationManagement: FC<IQuotationManagement> = ({ details }) => {
  const [isConfirmationVisible, setIsConfirmationVisible] = useState(false);
  const [isConfirmationButtonActive, setIsConfirmationButtonActive] = useState(true);
  const [selectedLineItems, setSelectedLineItems] = useState<ISelectedItems>({});
  const [isQuotationSubmitted, setIsQuotationSubmitted] = useState(false);
  const [isQuotationProcessing, setIsQuotationProcessing] = useState(false);
  const { freightRepository } = useRepository();
  const facilityId = +localStorage.getItem('facility')!;
  const { id: uuid } = useParams<{ id: string }>();

  const { data, isLoading } = useQuery<IProvidersQuotationResponse>(
    [`quotation-management-${facilityId}-${uuid}`],
    () => freightRepository.getProvidersQuotation(facilityId, uuid ?? ''),
    {
      refetchOnWindowFocus: false,
    },
  );

  useEffect(() => {
    if (data && !data?.is_assignment_possible) {
      // restore values if quotation were submitted

      const restoredLineItems: ISelectedItems = {};

      const lineItemsData = data.quotations_for_line_item;

      sortBy(lineItemsData, ['line_item_order_number'], ['asc']).forEach((item, index) => {
        Object.keys(item).forEach((key) => {
          if (item[key].selected) {
            restoredLineItems[key] = [...(restoredLineItems[key] || []), index];
          }
        });
      });

      setSelectedLineItems(restoredLineItems);
    }
  }, [data]);

  const sendProvidersQuotation = useMutation(
    (payload: string[]) =>
      freightRepository.sendProvidersQuotation(facilityId, uuid as string, payload),
    {
      onSuccess: () => {
        setIsConfirmationVisible(false);
        setIsConfirmationButtonActive(false);
        setIsQuotationSubmitted(true);
        message.success('Email notification has been send to the selected providers');
      },
      onError: () => {
        message.error('Request failed, please try again.');
      },
      onSettled: () => {
        setIsQuotationProcessing(false);
      },
    },
  );

  const mapLineItemIds = () => {
    const lineValues = sortBy(
      values(data && data.quotations_for_line_item),
      ['line_item_order_number'],
      ['asc'],
    );
    const keys = Object.keys(selectedLineItems);

    const result: string[] = keys.reduce((aggregator, element) => {
      const idList = selectedLineItems[element].map((index) => {
        return lineValues[index][element]?.line_item_quotation?.uuid || '';
      });

      return [...aggregator, ...idList];
    }, [] as string[]);

    setIsQuotationProcessing(true);

    sendProvidersQuotation.mutate(result);
  };

  const currencies = data && getCurrencies(data);

  const isButtonActive = () => {
    return Object.keys(selectedLineItems).some((key) => selectedLineItems[key].length);
  };

  const updateRequestMutation = useMutation(
    (detailsUpdated: IDetailsUpdate) =>
      freightRepository.updateRequestDetails(facilityId, uuid as string, detailsUpdated),
    {
      onSuccess: () => {
        window.location.reload();
      },
      onError: onErrorHandler,
    },
  );

  const statusChangeHandler = (value: string) => {
    updateRequestMutation.mutate({ status: value });
  };

  const destinations = getDestinations(data, selectedLineItems);

  return (
    <Spin spinning={isLoading}>
      <ConfirmationModal
        isConfirmationVisible={isConfirmationVisible}
        setIsConfirmationVisible={setIsConfirmationVisible}
        onSubmitHandler={mapLineItemIds}
        isButtonDisabled={isQuotationProcessing}
      />
      <div className="booking-details-container hbh-container">
        <div className="details-header">
          <div className="dropdown-container">
            <Dropdown
              styles={statusDropdownCustomStyles}
              components={getDropdownStatusComponentProps(
                !details?.allowed_transitions?.length,
                details?.status ?? '',
              )}
              isSearchable={false}
              onChange={(option) => {
                statusChangeHandler(option.value);
              }}
              options={details?.allowed_transitions as any}
              value={{
                label: details?.status_name,
                value: details?.status,
              }}
            />
          </div>
        </div>
        {data?.quotations_submission_date && data.quotations_submission_date.display ? (
          <SubmissionDeadline
            date={data?.quotations_submission_date.deadline}
            isDeadlineReached={data?.quotations_submission_date.deadline_reached || true}
            days={data?.quotations_submission_date.days_left || ''}
          />
        ) : null}
        <OverviewList data={data?.providers_quotations_overview ?? []} />
        <LineItemAssignment
          selectedLineItems={selectedLineItems}
          setSelectedLineItems={
            data?.is_assignment_possible && !isQuotationSubmitted
              ? setSelectedLineItems
              : () => null
          }
          currencies={currencies ?? {}}
          data={data?.quotations_for_line_item}
        />
        <div>
          {destinations.map(({ uuid: id, name, providers }) => {
            const items = Object.keys(providers).map((key: any) => {
              return {
                label: key,
                content: `${Number(providers[key].toFixed(2)).toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                })} USD`,
                value: providers[key],
              };
            });

            const sum = items.reduce((previousValue, currentValue) => {
              return previousValue + currentValue.value;
            }, 0);

            items.push({
              label: 'OVERALL COST',
              content: `${Number(sum.toFixed(2)).toLocaleString('en-US', {
                minimumFractionDigits: 2,
              })} USD`,
              value: null,
            });

            return <DetailsContainer key={id} title={`Destination - ${name}`} items={items} />;
          })}
        </div>
        <AssignmentDetails
          selectedLineItems={selectedLineItems}
          data={data?.quotations_for_line_item}
        />
        {isConfirmationButtonActive && data?.is_assignment_possible && (
          <Button
            onClick={() => isButtonActive() && setIsConfirmationVisible(true)}
            variant="submit"
            disabled={!isButtonActive()}
            className="quotation-button"
            text="CONFIRM FREIGHT ASSIGNMENT"
          />
        )}
      </div>
    </Spin>
  );
};

export default QuotationManagement;
